import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    List,
    Box,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Menu,
    MenuItem,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    Grid,
    FormControlLabel,
    Checkbox,
    Card,
    CardContent,
    Collapse,
    Chip,
    IconButton
} from '@mui/material';
import { Delete, Add, FilterList, ExpandMore, ExpandLess, Translate, Edit, Article } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import AlgorithmicProblemForm from './AlgorithmicProblemForm';
import AlgorithmicProblemView from './AlgorithmicProblemView';
import AlgorithmicProblemListItem from './AlgorithmicProblemListItem';
import PageContainer from '../layout/PageContainer';

const AlgorithmicProblemList = () => {
    const navigate = useNavigate();
    const [problems, setProblems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Search and filter states - load from localStorage or use defaults
    const [searchTerm, setSearchTerm] = useState(() => {
        const saved = localStorage.getItem('algorithmicProblemList_searchTerm');
        return saved || '';
    });
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => {
        const saved = localStorage.getItem('algorithmicProblemList_searchTerm');
        return saved || '';
    });
    const [languages, setLanguages] = useState([]);
    
    // Filter states - load from localStorage or use defaults
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [difficulty, setDifficulty] = useState(() => {
        const saved = localStorage.getItem('algorithmicProblemList_difficulty');
        return saved || '';
    });
    const [isReadyToServe, setIsReadyToServe] = useState(() => {
        const saved = localStorage.getItem('algorithmicProblemList_isReadyToServe');
        return saved === 'true';
    });
    
    // Pagination states - load from localStorage or use defaults
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = localStorage.getItem('algorithmicProblemList_currentPage');
        return saved ? parseInt(saved, 10) : 1;
    });
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem('algorithmicProblemList_pageSize');
        return saved ? parseInt(saved, 10) : 10;
    });
    const [totalPages, setTotalPages] = useState(0);
    const [totalProblems, setTotalProblems] = useState(0);
    
    // Dialog states
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedProblem, setSelectedProblem] = useState(null);

    // Menu states
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuProblemId, setMenuProblemId] = useState(null);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            const savedSearchTerm = localStorage.getItem('algorithmicProblemList_searchTerm') || '';
            
            // Update debounced search term
            setDebouncedSearchTerm(searchTerm);
            
            // Only reset to page 1 if this is a user-initiated search change
            if (searchTerm !== savedSearchTerm) {
                setCurrentPage(1);
            }
        }, 500); // 500ms delay

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    useEffect(() => {
        fetchProblems();
    }, [currentPage, pageSize, debouncedSearchTerm, difficulty, isReadyToServe]);

    // Fetch languages once when component mounts
    useEffect(() => {
        fetchLanguages();
    }, []);

    // Save pagination settings to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('algorithmicProblemList_currentPage', currentPage.toString());
    }, [currentPage]);

    useEffect(() => {
        localStorage.setItem('algorithmicProblemList_pageSize', pageSize.toString());
    }, [pageSize]);

    // Save search term to localStorage
    useEffect(() => {
        localStorage.setItem('algorithmicProblemList_searchTerm', searchTerm);
    }, [searchTerm]);

    // Save filters to localStorage
    useEffect(() => {
        localStorage.setItem('algorithmicProblemList_difficulty', difficulty);
    }, [difficulty]);

    useEffect(() => {
        localStorage.setItem('algorithmicProblemList_isReadyToServe', isReadyToServe.toString());
    }, [isReadyToServe]);

    const fetchProblems = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString(),
                ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
                ...(difficulty && { difficulty }),
                ...(isReadyToServe && { isReadyToServe: 'true' })
            });
            
            const response = await userRequest.get(`/algorithmic-problem?${params}`);
            setProblems(response.data.problems);
            setTotalPages(response.data.pagination.totalPages);
            setTotalProblems(response.data.pagination.totalProblems);
            
            // If current page is greater than total pages, reset to last page
            if (currentPage > response.data.pagination.totalPages && response.data.pagination.totalPages > 0) {
                setCurrentPage(response.data.pagination.totalPages);
            }
            
            setError('');
        } catch (error) {
            console.error('Error fetching problems:', error);
            setError('Failed to fetch problems');
        } finally {
            setLoading(false);
        }
    };

    const fetchLanguages = async () => {
        try {
            const response = await userRequest.get('/language');
            
            if (response.data && Array.isArray(response.data)) {
                setLanguages(response.data);
            } else {
                setLanguages([]);
            }
        } catch (error) {
            console.error('Error fetching languages:', error);
            setLanguages([]);
        }
    };

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setDifficulty('');
        setIsReadyToServe(false);
        setCurrentPage(1);
        localStorage.setItem('algorithmicProblemList_difficulty', '');
        localStorage.setItem('algorithmicProblemList_isReadyToServe', 'false');
    };

    const handleCreateProblem = () => {
        setEditMode(false);
        setSelectedProblem(null);
        setFormDialogOpen(true);
    };

    const handleEditProblem = (problem) => {
        setEditMode(true);
        setSelectedProblem(problem);
        setFormDialogOpen(true);
    };

    const handleViewProblem = (problem) => {
        setSelectedProblem(problem);
        setViewDialogOpen(true);
    };

    const handleTranslateProblem = (problem) => {
        if (problem.task?._id) {
            navigate(`/text/translate/${problem.task._id}`);
        } else {
            setError('Cannot open translation editor: Task Text ID not found');
        }
    };

    const handleEditContent = (problem) => {
        navigate(`/algorithmic-problems/edit/${problem._id}`);
    };

    const handleDeleteProblem = async (problemId, problemTitle) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${problemTitle}"? This will also delete the associated texts and translations.`
        );
        
        if (!confirmed) return;

        try {
            await userRequest.delete(`/algorithmic-problem/${problemId}`);
            
            // If we're on the last page and this is the only item, go to previous page
            if (problems.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                // Refresh current page
                await fetchProblems();
            }
        } catch (error) {
            console.error('Error deleting problem:', error);
            setError('Failed to delete problem');
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (editMode && selectedProblem) {
                // Update existing problem
                await userRequest.patch(`/algorithmic-problem/${selectedProblem._id}`, formData);
            } else {
                // Create new problem
                await userRequest.post('/algorithmic-problem', formData);
                setCurrentPage(1);
            }
            
            await fetchProblems();
            setFormDialogOpen(false);
            setError('');
        } catch (error) {
            console.error('Error saving problem:', error);
            setError('Failed to save problem');
        }
    };

    const handleCloseForm = () => {
        setFormDialogOpen(false);
        setSelectedProblem(null);
        setEditMode(false);
    };

    const handleCloseView = () => {
        setViewDialogOpen(false);
        setSelectedProblem(null);
    };

    const handleMenuOpen = (event, problem) => {
        setAnchorEl(event.currentTarget);
        setMenuProblemId(problem._id);
        setSelectedProblem(problem);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuProblemId(null);
        setSelectedProblem(null);
    };

    const handleMenuDelete = () => {
        if (menuProblemId && selectedProblem) {
            handleDeleteProblem(menuProblemId, selectedProblem.title);
            handleMenuClose();
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Typography variant="h4">Loading...</Typography>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Algorithmic Problems</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={handleCreateProblem}
                >
                    Add New Problem
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TextField
                fullWidth
                label="Search problems..."
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ mb: 2 }}
                placeholder="Search by title or tags"
            />

            {/* Filter Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ pb: filterExpanded ? 2 : 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <FilterList />
                            <Typography variant="h6">Filters</Typography>
                            {(difficulty || isReadyToServe) && (
                                <Chip 
                                    label={`${(difficulty ? 1 : 0) + (isReadyToServe ? 1 : 0)} active`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            {(difficulty || isReadyToServe) && (
                                <Button 
                                    size="small" 
                                    onClick={handleClearFilters}
                                    variant="outlined"
                                >
                                    Clear All
                                </Button>
                            )}
                            <IconButton onClick={() => setFilterExpanded(!filterExpanded)}>
                                {filterExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Box>
                    </Box>
                    
                    <Collapse in={filterExpanded}>
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Difficulty</InputLabel>
                                        <Select
                                            value={difficulty}
                                            label="Difficulty"
                                            onChange={(e) => {
                                                setDifficulty(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="easy">Easy</MenuItem>
                                            <MenuItem value="medium">Medium</MenuItem>
                                            <MenuItem value="hard">Hard</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isReadyToServe}
                                                onChange={(e) => {
                                                    setIsReadyToServe(e.target.checked);
                                                    setCurrentPage(1);
                                                }}
                                            />
                                        }
                                        label="Ready to Serve"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">
                    {totalProblems} {totalProblems === 1 ? 'problem' : 'problems'} found
                </Typography>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Items per page</InputLabel>
                    <Select
                        value={pageSize}
                        label="Items per page"
                        onChange={handlePageSizeChange}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <List>
                {problems.map((problem, index) => {
                    const orderNumber = (currentPage - 1) * pageSize + index + 1;
                    return (
                        <AlgorithmicProblemListItem
                            key={problem._id}
                            problem={problem}
                            orderNumber={orderNumber}
                            onView={handleViewProblem}
                            onEdit={handleEditProblem}
                            onTranslate={handleTranslateProblem}
                            onEditContent={handleEditContent}
                            onMenuOpen={handleMenuOpen}
                        />
                    );
                })}
            </List>

            {problems.length === 0 && !loading && (
                <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary">
                        No problems found
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleCreateProblem}
                        sx={{ mt: 2 }}
                    >
                        Create Your First Problem
                    </Button>
                </Box>
            )}

            {/* Form Dialog */}
            <Dialog open={formDialogOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editMode ? 'Edit Algorithmic Problem' : 'Create New Algorithmic Problem'}
                </DialogTitle>
                <DialogContent>
                    <AlgorithmicProblemForm
                        problem={selectedProblem}
                        languages={languages}
                        onSubmit={handleFormSubmit}
                        onCancel={handleCloseForm}
                    />
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {selectedProblem?.title}
                </DialogTitle>
                <DialogContent>
                    <AlgorithmicProblemView problem={selectedProblem} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseView}>Close</Button>
                    {selectedProblem && (
                        <>
                            <Button 
                                onClick={() => handleTranslateProblem(selectedProblem)} 
                                startIcon={<Translate />}
                                color="primary"
                            >
                                Translate Task
                            </Button>
                            <Button 
                                onClick={() => handleEditContent(selectedProblem)} 
                                startIcon={<Article />}
                                color="secondary"
                            >
                                Edit Content
                            </Button>
                            <Button onClick={() => handleEditProblem(selectedProblem)} startIcon={<Edit />}>
                                Edit
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Menu Dialog */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
                    <Delete sx={{ mr: 1 }} />
                    Delete Problem
                </MenuItem>
            </Menu>

            <Grid container justifyContent="center" sx={{ mt: 3 }}>
                {totalPages > 1 && (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(event, value) => {
                                setCurrentPage(value);
                            }}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                        <Typography variant="body2" color="text.secondary">
                            Page {currentPage} of {totalPages} 
                            ({Math.min((currentPage - 1) * pageSize + 1, totalProblems)}-{Math.min(currentPage * pageSize, totalProblems)} of {totalProblems} problems)
                        </Typography>
                    </Box>
                )}
            </Grid>
        </PageContainer>
    );
};

export default AlgorithmicProblemList;

